import { queriesPayrollRuns } from "@query";
import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSList
} from "@wingspanhq/fe-component-library";
import {
  ListPayrollRunsFilterParameter,
  PayrollRunType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import { useUrlQuery } from "../../../../shared/hooks/useUrlQuery";
import { useDownloadPayrollRunsCsv } from "../../hooks/useDownloadPayrollRunsCsv";
import { PayrollRunsYear } from "../../paths";
import { selectorPayrollRunsFilterStatuses } from "../../selectors/selectorPayrollRunsFilterStatuses";
import { getPayrollRunYearDateRange } from "../../utils";

import { TablePayrollsEmployee } from "./TablePayrollsEmployee";
import { buildPayrollsFiltersConfig } from "./filters/config";
import { PAYROLLS_CYCLE_FILTER_ALL, PayrollsFilter } from "./filters/types";

type Props = {
  /** Bounds the list to one calendar year via `filter.payDate`. */
  year: PayrollRunsYear;
  onRowOpen: (id: string) => void;
};

/**
 * The employee payroll history surface: the new design's table, with the status
 * and cycle filters above it and a CSV export in the header.
 *
 * Employee runs only — the Payables tab lists contractor runs through its own
 * screen, which keeps the retired v1 layout instead.
 */
export const FilterableTablePayrollsEmployee: React.FC<Props> = props => {
  const { urlQuery, setUrlQuery } = useUrlQuery<PayrollsFilter>();
  const filtersConfig = useMemo(() => buildPayrollsFiltersConfig(), []);

  const rawCycle = urlQuery.filter.cycleType;
  const cycleType =
    rawCycle && rawCycle !== PAYROLLS_CYCLE_FILTER_ALL ? rawCycle : undefined;
  // Memoised on the raw URL value (itself stable per `location.search`) so the
  // request filter below keeps a stable identity across renders.
  const statuses = useMemo(
    () => selectorPayrollRunsFilterStatuses(urlQuery.filter.status),
    [urlQuery.filter.status]
  );
  const hasUserFilters = !!cycleType || !!statuses;

  // Server-paginated + server-filtered: listPayrollRuns accepts page + filter (no
  // sort). The payroll type and the selected year are the base filter; the status
  // checkboxes and the cycle radio compose on top. No client-side filter/sort
  // over flatData.
  //
  // Unlike the Active payrolls tab this does NOT constrain status — per the design
  // the history table lists every run of the year and the filter narrows it.
  const filter = useMemo<ListPayrollRunsFilterParameter>(
    () => ({
      type: { eq: PayrollRunType.EMPLOYEE },
      payDate: getPayrollRunYearDateRange(props.year),
      ...(statuses ? { status: { anyOf: statuses } } : {}),
      ...(cycleType ? { cycleType: { anyOf: [cycleType] } } : {})
    }),
    [props.year, statuses, cycleType]
  );

  const query = queriesPayrollRuns.listPayrollRuns.usePaginatedQuery({
    filter
  });

  const payrollRuns = query.flatData ?? [];

  // Count comes from the response pagination summary, not flatData.length.
  const totalCount = query.data?.pages?.[0]?.pagination?.totalSize;

  const { downloadCsv, isLoading: isDownloadingCsv } =
    useDownloadPayrollRunsCsv({
      filter,
      filename: `Payroll runs ${props.year}`
    });

  const renderContent = () => {
    if (query.isInitialLoading) {
      return <TablePayrollsEmployee items={[]} loading />;
    }

    if (query.isError) {
      return (
        <WSEmptyState
          actions={{
            buttons: [{ label: "Try again", onClick: () => query.refetch() }]
          }}
          description="Something went wrong loading payroll runs."
          title="Couldn't load payroll history"
          type="error"
        />
      );
    }

    if (payrollRuns.length === 0) {
      return hasUserFilters ? (
        <WSEmptyState
          description="No payroll runs match the selected filters."
          title="No matching payroll runs"
          type="search"
        />
      ) : (
        <WSEmptyState
          description="Payroll runs for the selected year will appear here."
          title="No payroll history yet"
          type="documents"
        />
      );
    }

    return (
      <WSInfiniteScroll
        endOfList={!query.hasNextPage}
        loading={!!query.isFetchingNextPage}
        loadMore={payrollRuns.length > 0}
        onLoad={() => query.fetchNextPage()}
      >
        <TablePayrollsEmployee
          downloading={isDownloadingCsv}
          items={payrollRuns}
          onDownload={downloadCsv}
          onRowOpen={props.onRowOpen}
        />
      </WSInfiniteScroll>
    );
  };

  return (
    <WSList gap="L">
      <WSFilters<PayrollsFilter>
        info={totalCount === undefined ? undefined : { count: totalCount }}
        primaryFilters={filtersConfig}
        values={urlQuery.filter}
        onFilter={newFilter =>
          setUrlQuery(prev => ({ ...prev, filter: newFilter }))
        }
      />
      {renderContent()}
    </WSList>
  );
};
